import { ChangeEvent, FC, SyntheticEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import CachedIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
	Box,
	Button,
	Paper,
	SxProps,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
	createStyles,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Theme } from "@mui/system";

import SharePointLogo from "@/assets/svg/external/Microsoft_Office_SharePoint_(2019–present).svg?react";

import { Translate } from "@/components/Application/Translate";
import { DocumentListItemIcon } from "@/components/Procedures/EditNode/DocumentListItemIcon";
import NodeDocumentUpload from "@/components/Procedures/EditNode/NodeDocumentUpload";

import { isSearchAnUrl } from "@/helpers/RegEx";
import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import SkeletonLoadingTable from "@/helpers/components/Loaders/SkeletonLoadingTable";
import Spinner from "@/helpers/components/Spinner";

import { DocumentUpdateRequest } from "@/model/Procedure/DocumentUpdateRequest";
import { DocumentForProcedureImport } from "@/model/Procedure/ProcedureUseDocument";
import { ImportProcedureNodeDocumentRequest } from "@/model/Procedure/UpdateProcedureNodeRequest";

import {
	ReimportDocument,
	UpdateProcedureNodeDocument,
} from "@/service/ProcedureMutations";
import { useSearchForProcedureDocument } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

const useStyles = (theme: Theme): { [key: string]: SxProps } =>
	createStyles({
		container: {
			display: "flex",
			padding: "20px",
		},
		containerColumn: {
			display: "flex",
			flexDirection: "column",
			padding: "20px",
		},
		selectedFileBox: {
			margin: "1rem 0 0 0",
			display: "flex",
		},
		tableContainer: {
			borderRadius: 2,
			overflow: "hidden",
			boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
			width: "90%",
			alignSelf: "center",
		},
		activeTab: {
			borderRadius: "8px 0 0 8px",
			boxShadow: "10px 10px 20px rgba(0, 0, 0, 0.1)",
			backgroundColor: theme.palette.primary.main,
		},
		smallTab: {
			minWidth: "76px",
			width: "50px",
			padding: "2px",
		},
		tableHeaderCell: {
			fontWeight: "bold",
		},
		tableIconColumn: {
			width: "100px",
		},
		tableItemRow: {
			cursor: "pointer",
			"&:hover": {
				backgroundColor: theme.palette.action.hover,
			},
			"&.selected": {
				backgroundColor: theme.palette.action.focus,
			},
		},
		uploadBox: {
			padding: "2rem",
		},
		saveButton: {
			margin: "1rem",
			position: "fixed",
			top: 50,
			right: 100,
		},
	});

interface FileListProps {
	search: string;
	onClose: () => void;
	nodeId: number;
}

const FileList: FC<FileListProps> = ({
	nodeId,
	onClose,
	search,
}: FileListProps) => {
	const { id } = useParams<RouteParams>();
	const theme = useTheme();
	const classes = useStyles(theme);
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	// Pagination state
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [files, setFiles] = useState<Array<DocumentForProcedureImport>>([]);
	const [selectedFile, setSelectedFile] = useState<
		DocumentForProcedureImport | undefined
	>(undefined);
	const [selectedFileIndex, setSelectedFileIndex] = useState<number>();
	const [currentTab, setTab] = useState<number>(0);
	const [fileType, setFileType] = useState<"SharePoint" | "Local">(
		"SharePoint",
	);
	const [isUrl, setIsUrl] = useState<boolean>(false);
	const [url, setUrl] = useState<string>("");
	const [reload, setReload] = useState<boolean>();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [updateLoading, setDocLoading] = useState<boolean>(false);
	const [documentUpdated, setDocUpdated] = useState<boolean>(false);

	const { data: documents, isLoading } = useSearchForProcedureDocument(search);

	useEffect(() => {
		if (documents && documents.length > 0) {
			setFiles([...documents]);
		} else if (isSearchAnUrl(search)) {
			setFiles([]);
		} else {
			setFiles([]);
		}
	}, [search, documents, isLoading]);

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeTab = (event: SyntheticEvent, newValue: number) => {
		newValue === 0 ? setFileType("SharePoint") : setFileType("Local");
		setTab(newValue);
	};

	const handleSelectFile = (
		event: unknown,
		file: DocumentForProcedureImport,
		key: number,
	) => {
		setSelectedFile(file);
		setSelectedFileIndex(key);
	};

	const updateProcedureNodeDocument = UpdateProcedureNodeDocument();

	const handleSave = async () => {
		setIsSubmitting(true);
		const request: ImportProcedureNodeDocumentRequest = {
			ProcedureId: +id,
			SchemeId: nodeId,
			DocumentId: selectedFile?.FileId,
			FileName: selectedFile?.Name,
			ExternalUniqueId: selectedFile?.ExternalUniqueId,
			IsUrl: isUrl,
			Url: url,
			Reload: reload,
		};

		try {
			const result = await updateProcedureNodeDocument(request);
			if (result.Success) {
				AlertRequest.success();
				onClose();
			} else {
				AlertRequest.failed();
				console.error(result.Message);
			}
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const reimportDocument = ReimportDocument();

	const handleUpdateDocument = async (
		fileId: number,
		uniqueId: string | undefined,
	) => {
		const documentUpdateRequest: DocumentUpdateRequest = {
			FileId: fileId,
			ExternalUniqueId: uniqueId,
			IsReload: true,
		};
		setDocLoading(true);
		try {
			await new Promise((resolve) => {
				setTimeout(resolve, 2000); // 2000 milliseconds (2 seconds)
			});

			await reimportDocument(documentUpdateRequest);
			setDocUpdated(true);
		} catch (error) {
			console.error(error);
		} finally {
			await new Promise((resolve) => {
				setTimeout(resolve, 2000); // 2000 milliseconds (2 seconds)
			});
		}
	};

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	if (isLoading) {
		return <SkeletonLoadingTable nRows={rowsPerPage} />;
	}

	if (isSubmitting) {
		return (
			<Spinner
				text={
					<Translate String="Procedure.Node.File.Saving">
						Saving document
					</Translate>
				}
			/>
		);
	}

	if (!files) {
		return (
			<Paper sx={classes.tableContainer}>
				<Typography>
					<Translate String="Procedure.Node.File.NotFound">
						Can't find files
					</Translate>
				</Typography>
			</Paper>
		);
	}

	const fileGroup = files.filter(
		(file) => file.DocumentLocationType === fileType && file.OriginalFilename,
	);

	return (
		<>
			{selectedFile && (
				<Box sx={classes.selectedFileBox}>
					<Box>
						<Translate String="Document.Search.SelectedFile">
							Selected file:
						</Translate>
						<Typography>{selectedFile?.OriginalFilename}</Typography>
					</Box>
					<Button
						color="error"
						onClick={(e) => {
							e.stopPropagation();
							setSelectedFile(undefined);
							setSelectedFileIndex(undefined);
						}}
						sx={{
							marginLeft: "10px",
							minWidth: "40px",
							minHeight: "40px",
							width: "40px",
							height: "40px",
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<DeleteIcon />
					</Button>
				</Box>
			)}
			<Box sx={isSmallScreen ? classes.containerColumn : classes.container}>
				<Tabs
					orientation={isSmallScreen ? "horizontal" : "vertical"}
					sx={{ alignSelf: isSmallScreen ? "center" : "" }}
					value={currentTab}
					variant={isSmallScreen ? "scrollable" : "fullWidth"}
					onChange={handleChangeTab}
					TabIndicatorProps={{ sx: classes.activeTab }}
				>
					<Tab
						sx={
							isSmallScreen
								? classes.smallTab
								: ("" as SxProps<Theme> | undefined)
						}
						icon={
							<SharePointLogo
								className="svg-icon svg-icon--logo"
								width={isSmallScreen ? "15" : "20"}
							/>
						}
						label={
							isSmallScreen ? (
								""
							) : (
								<Translate String="Filelist.Tabs.Sharepoint">
									SharePoint
								</Translate>
							)
						}
					/>
					<Tab
						sx={
							isSmallScreen
								? classes.smallTab
								: ("" as SxProps<Theme> | undefined)
						}
						icon={
							<InsertDriveFileIcon
								className="svg-icon svg-icon--logo"
								width={isSmallScreen ? "10" : "20"}
							/>
						}
						label={
							isSmallScreen ? (
								""
							) : (
								<Translate String="Filelist.Tabs.Local">Local files</Translate>
							)
						}
					/>
					<Tab
						sx={
							isSmallScreen
								? classes.smallTab
								: ("" as SxProps<Theme> | undefined)
						}
						icon={
							<LinkIcon
								className="svg-icon svg-icon--logo"
								width={isSmallScreen ? "10" : "20"}
							/>
						}
						label={
							isSmallScreen ? (
								""
							) : (
								<Translate String="Filelist.Tabs.Url">Url</Translate>
							)
						}
					/>
					<Tab
						sx={
							isSmallScreen
								? classes.smallTab
								: ("" as SxProps<Theme> | undefined)
						}
						icon={
							<UploadFileIcon
								className="svg-icon svg-icon--logo"
								width={isSmallScreen ? "10" : "20"}
							/>
						}
						label={
							isSmallScreen ? (
								""
							) : (
								<Translate String="Filelist.Tabs.Upload">Upload</Translate>
							)
						}
					/>
				</Tabs>

				<Paper sx={classes.tableContainer}>
					{currentTab < 2 ? (
						<>
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell sx={classes.tableHeaderCell}>
												<Translate String="Filelist.Header.Filetype">
													File Type
												</Translate>
											</TableCell>

											<TableCell sx={classes.tableHeaderCell}>
												<Translate String="Filelist.Header.Filename">
													File Name
												</Translate>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{fileGroup
											.slice(
												page * rowsPerPage,
												page * rowsPerPage + rowsPerPage,
											)
											.map((file, key) => (
												<TableRow
													key={key}
													className={`${classes.tableItemRow} ${
														selectedFileIndex === key &&
														selectedFile?.DocumentLocationType ===
															file.DocumentLocationType
															? "selected"
															: ""
													}`}
												>
													<TableCell>
														<DocumentListItemIcon
															documentType={file.DocumentLocationType}
														/>
													</TableCell>
													<TableCell
														onClick={(e) => handleSelectFile(e, file, key)}
													>
														{file.OriginalFilename}
													</TableCell>
													<TableCell>
														{file.DocumentLocationSource != "Local" ? (
															<Tooltip
																title={
																	<Translate String="Procedures.UsedDocuments.Update.Document">
																		Update document
																	</Translate>
																}
															>
																<Button
																	variant="text"
																	color={documentUpdated ? "success" : "info"}
																	onClick={(e) =>
																		handleUpdateDocument(
																			file.FileId,
																			file.ExternalUniqueId,
																		)
																	}
																>
																	<CachedIcon />
																</Button>
															</Tooltip>
														) : (
															<></>
														)}
													</TableCell>
												</TableRow>
											))}
									</TableBody>
								</Table>
							</TableContainer>
							<TablePagination
								rowsPerPageOptions={[5, 10, 20]}
								component="div"
								count={fileGroup.length}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
							/>
							<Button
								variant="outlined"
								color="success"
								sx={classes.saveButton}
								onClick={handleSave}
								disabled={isSubmitting || updateLoading}
							>
								<Translate String="FileList.Save">Save</Translate>
							</Button>
						</>
					) : currentTab === 3 ? (
						<NodeDocumentUpload
							onClose={onClose}
							nodeId={nodeId}
							isUploading={isSubmitting || updateLoading}
						/>
					) : (
						<Box sx={classes.uploadBox}>
							<Translate
								String="Filelist.Tabs.Url.Label"
								HTMLTag="label"
								htmlFor="Url"
							>
								Url to file
							</Translate>
							<input
								id="Url"
								type="text"
								onChange={(e) => {
									setUrl(e.target.value);
									setIsUrl(true);
								}}
							/>
							<Button
								onClick={handleSave}
								variant="outlined"
								sx={classes.saveButton}
								color="success"
							>
								Save
							</Button>
						</Box>
					)}
				</Paper>
			</Box>
		</>
	);
};

export default FileList;
